import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router';
import { useSearchParams } from 'react-router-dom';

// API/Components
import { DATE_RANGES } from '../components/enums';
import axiosInstance from '../components/axios';
import useHotkeyHandlers from '../components/hooks';
import UnitTrendReport from '../components/unit_trend_report';
import UnitTrendGraph from '../components/unit_trend_graph';

// Material UI
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Switch from '@mui/material/Switch';
import Stack from '@mui/material/Stack';
import Toolbar from '@mui/material/Toolbar';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import { styled } from '@mui/material/styles';
import { Button } from '@mui/material';

const DataSwitch = styled(Switch)(({ theme }) => ({
	width: 30,
	height: 18,
	padding: 0,
	display: 'flex',
	'& .MuiSwitch-switchBase': {
		padding: 2,
		'&.Mui-checked': {
			transform: 'translateX(14px)',
			color: '#fff',
			'& + .MuiSwitch-track': {
				opacity: 1,
				backgroundColor: '#B1282E',
			},
		},
	},
	'& .MuiSwitch-thumb': {
		boxShadow: '0 2px 4px 0 rgb(0 35 11 / 20%)',
		width: 12,
		height: 14,
		borderRadius: 2,
		transition: theme.transitions.create(['width'], {
			duration: 200,
		}),
	},
	'& .MuiSwitch-track': {
		borderRadius: 4,
		opacity: 1,
		backgroundColor: '#0F1820',
		boxSizing: 'border-box',
	},
}));

// Unit Navigation Constants
const UNIT_NAV = {
	BACK: -1,
	FORWARD: 1,
}

// Unit Trend sub-page rendering (Location Name, Title, String Selection, Unit Selection)
export default function UnitTrend() {
	const { locationNum } = useParams();
	const [ searchParams ] = useSearchParams();

	// State Management
	const [locationInfo, setLocationInfo] = useState({
		locationName: '',
		numStrings: 0,
		numUnits: 0,
	});
	
	const [selectedDateRange, setSelectedDateRange] = useState(DATE_RANGES[0].value);
	const [selectedString, setSelectedString] = useState();
	const [selectedUnit, setSelectedUnit] = useState();

	const [trendData, setTrendData] = useState({});
	const [limitData, setLimitData] = useState({});
	
	const [isLoading, setIsLoading] = useState(true);
	const [graphDisplay, setGraphDisplay] = useState(false);
	const [graphType, setGraphType] = useState(0);
	
	const numStringsRef = useRef();
	const numUnitsRef = useRef();
	const selectedStringRef = useRef();
	const selectedUnitRef = useRef();
	const graphTypeRef = useRef();
	
	// useEffect(() => {
	// 	console.log(searchParams.get("string"));
	// }, [searchParams])

	// Handle the current selectedString for hotkey nav
	useEffect(() => {
		numStringsRef.current = locationInfo.numStrings;
		numUnitsRef.current = locationInfo.numUnits;
	}, [locationInfo]);

	// Handle the current selectedString for hotkey nav
	useEffect(() => {
		selectedStringRef.current = selectedString;
	}, [selectedString]);

	// Handle the current selectedUnit for hotkey nav
	useEffect(() => {
		selectedUnitRef.current = selectedUnit;
	}, [selectedUnit]);

	// Handle the current graphType for hotkey nav
	useEffect(() => {
		graphTypeRef.current = graphType;
	}, [graphType]);

	// Handle keypresses for unit and graph navigation
	useHotkeyHandlers([
		{
			key: 'PageUp',
			down: (e) => {
				e.preventDefault();  // Prevent default scrolling behavior
				handleUnitNavigation(UNIT_NAV.BACK);
			},
			up: () => {},
		},
		{
			key: 'PageDown',
			down: (e) => {
				e.preventDefault();
				handleUnitNavigation(UNIT_NAV.FORWARD);
			},
			up: () => {},
		},
		{
			key: 'F8',
			down: (e) => {
				e.preventDefault();
				switch(graphTypeRef.current) {
					case 0:  // Voltage -> Impedance
						setGraphType(1);
						break;
					case 1:  // Impedance -> Temperature
						setGraphType(2);
						break;
					case 2:  // Temperature -> Voltage
						setGraphType(0);
						break;
					case 3:  // Diff Impedance -> Impedance
						setGraphType(1);
						break;
					default:
						setGraphType(0);
						break;
				}
			},
			up: () => {},
		},
		{
			key: 'F9',
			down: (e) => {
				e.preventDefault();
				if (graphTypeRef.current === 1) {  // Impedance -> Diff Impedance
					setGraphType(3);
				} else if (graphTypeRef.current === 3) {  // Diff Impedance -> Impedance
					setGraphType(1);
				}
			},
			up: () => {},
		},
	]);

	// Handle unit navigation to allow for unit wrapping when hitting the beginning/end of a string
	function handleUnitNavigation(unitNav) {
		const numStrings = numStringsRef.current;
		const numUnits = numUnitsRef.current;

		const stringNum = selectedStringRef.current;
		const unitNum = selectedUnitRef.current;

		if (selectedStringRef.current > 0 && selectedUnitRef.current > 0) {
			if (unitNav == UNIT_NAV.BACK) {
				if (unitNum > 1) {  // Decrement unit
					setSelectedUnit(Number(unitNum - 1));
				} else if (unitNum === 1 && stringNum > 1) {  // Wrap selected unit to last unit of previous string
					setSelectedString(Number(stringNum - 1));
					setSelectedUnit(numUnits);
				}
			}

			if (unitNav == UNIT_NAV.FORWARD) {
				if (unitNum < numUnits) {  // Increment unit
					setSelectedUnit(Number(unitNum + 1));
				} else if (unitNum === numUnits && stringNum < numStrings) {  // Wrap selected unit to first unit of next string
					setSelectedString(Number(stringNum + 1));
					setSelectedUnit(1);
				}
			}
		}
	}
	
	// Get information about the location id specified (name, size, num of temps)
	useEffect(() => {
		axiosInstance.get(`/loc/${locationNum}/size/`).then(response => {
			const locationInfo = response.data;
			setLocationInfo({
				locationName: locationInfo.location_name,
				serialNumber: locationInfo.serial_number,
				numStrings: locationInfo.num_strings,
				numUnits: locationInfo.num_units_per_string,
			});
			setSelectedString(Number(searchParams.get("string")) || 1);
			setSelectedUnit(Number(searchParams.get("unit")) || 1);
		});
	}, [locationNum, searchParams]);
	
	// Get the list of trend data for the specified location id, string number, and unit number
	useEffect(() => {
		setIsLoading(true);

		if (selectedString !== undefined || selectedUnit !== undefined) {
			axiosInstance.get(`/loc/${locationNum}/meas/string_${selectedString}/unit_${selectedUnit}?filter=${selectedDateRange}`).then(response => {
				const trendData = response.data;
				setTrendData(trendData);
			});
		}

		setIsLoading(false);
	}, [locationNum, selectedDateRange, selectedString, selectedUnit]);

	// Event Handlers
	const handleGraphTypeChange = (event) => {
		setGraphType(Number(event.target.value));
	}
	
	const handleDateRangeChange = (event) => {
		setSelectedDateRange(event.target.value);
	};

	const handleStringChange = (event) => {
		setSelectedString(Number(event.target.value));
	}
	
	const handleUnitChange = (event) => {
		setSelectedUnit(Number(event.target.value));
	}
	
	const handleViewChange = (event) => {
		setGraphDisplay(event.target.checked);
	}
		
	return (
		<Paper
			padding='true'
			sx={{
				overflow: 'auto',
				boxShadow: '0',
				height: '100%',
				display: 'flex',
				flexDirection: 'column',
			}}
		>
			<Typography
				variant='h5'
				fontFamily='Saira, sans-serif'
				fontWeight='600'
				color='#0F1820'
				align='center'
				padding='5px'
			>
				{ locationInfo.locationName } (SN {locationInfo.serialNumber})
			</Typography>
			<Toolbar>
				<Grid container spacing={1} alignItems="center">
					<Grid item xs alignItems="left">
						<Typography
							align="left"
							fontFamily='Saira,sans-serif'
							fontWeight='600'
							sx={{ flexGrow: 1}}
						>
							Unit Trend - String {selectedString} Unit {selectedUnit}
						</Typography>
					</Grid>
					{graphDisplay &&
						<Grid item>
							<FormControl sx={{ m: 1, minWidth: 100 }}>
								<label id="graph-type-select" style={{fontFamily:"Saira, sans-serif"}}>Graph Type</label>
								<select
									id="graph-type-select"
									value={graphType}
									label="Graph Type"
									onChange={handleGraphTypeChange}
								>
									<option key={0} value={0}>Voltage</option>
									<option key={1} value={1}>Impedance</option>
									<option key={3} value={3}>Diff. Impedance</option>
									<option key={2} value={2}>Temperature</option>
								</select>
							</FormControl>
						</Grid>
					}
					<Grid item>
						<FormControl sx={{ m: 0, minWidth: 80 }}>
							<label id="range-select" style={{fontFamily:"Saira, sans-serif"}}>Date Range</label>
							<select
								id="range-select"
								value={selectedDateRange}
								label="Date Range"
								onChange={handleDateRangeChange}
								sx= {{ maxHeight: 500 }}
							>
								{ DATE_RANGES.map((range) => (
									<option key={range.value} value={range.value}>{range.name}</option>
								)) }
							</select>
						</FormControl>
					</Grid>
					<Grid item>
						<FormControl sx={{ m: 0, minWidth: 80 }}>
							<label id="string-select" style={{fontFamily:"Saira, sans-serif"}}>String</label>
							<select
								id="string-select"
								value={selectedString}
								label="String"
								onChange={handleStringChange}
								sx= {{ maxHeight: 500 }}
							>
								{ Array.from({length: locationInfo.numStrings}, (_, i) => i + 1).map((stringNum) => (
									<option key={stringNum} value={stringNum}>String {stringNum}</option>
								)) }
							</select>
						</FormControl>
					</Grid>
					<Grid item>
						<FormControl sx={{ m: 0, minWidth: 80 }}>
							<label id="unit-select" style={{fontFamily:"Saira, sans-serif"}}>Unit</label>
							<select
								id="unit-select"
								value={selectedUnit}
								label="Unit"
								onChange={handleUnitChange}
								sx= {{ maxHeight: 500 }}
							>
								{ Array.from({length: locationInfo.numUnits}, (_, i) => i + 1).map((unitNum) => (
									<option key={unitNum} value={unitNum}>Unit {unitNum}</option>
								)) }
							</select>
						</FormControl>
					</Grid>
					<Grid item>
						<Tooltip title="Data display type">
							<Stack direction="row" spacing={1} alignItems="center">
								<Typography fontFamily='Saira, sans-serif'>Report</Typography>
									<DataSwitch checked={graphDisplay} onChange={handleViewChange} disabled={isLoading}/>
								<Typography fontFamily='Saira, sans-serif'>Graph</Typography>
							</Stack>
						</Tooltip>
					</Grid>
				</Grid>
			</Toolbar>
			{ graphDisplay ?
				<UnitTrendGraph
					trendData={trendData}
					selectedDateRange={selectedDateRange}
					selectedString={selectedString}
					selectedUnit={selectedUnit}
					graphType={graphType}
					loading={isLoading}
				/>
			:
				<UnitTrendReport
					trendData={trendData}
					loading={isLoading}
				/>
			}
		</Paper>
	);
}